import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import clsx from "clsx";
import {
  btnLinkCls,
  btnPrimaryCls,
  btnSecondaryCls,
} from "components/tw/classes";
import {
  CatalogTable,
  ChangeLink,
  SettingsCard,
  StatusBanner,
  useSaveWithToast,
} from "containers/settings/ui";
import { fetchCompanyHolidays, saveCompanyHoliday } from "./companyHolidays.service";
import { CompanyHoliday } from "./companyHolidays.types";
import { HolidayDraft, HolidayForm } from "./HolidayForm";
import { getPublicHolidaysForYear } from "./publicHolidaysData";

const NEW_HOLIDAY: HolidayDraft = { title: "", startDate: "", endDate: "" };

/** One day as "2026-12-24", several as "2026-12-24 – 2026-12-26". */
function formatDates(holiday: CompanyHoliday) {
  return holiday.startDate === holiday.endDate
    ? holiday.startDate
    : `${holiday.startDate} – ${holiday.endDate}`;
}

/**
 * Which holidays do not count as leave days, year by year. Public holidays can
 * be loaded from a built-in list, company ones are added by hand. The
 * Saturday/Sunday rest-day switches are left out for now; the backend keeps
 * counting the weekend as it is set.
 */
export default function CompanyHolidaysSettingsPage() {
  const { t } = useTranslation();
  const { companyId = "" } = useParams<{ companyId: string }>();
  const { save, saving } = useSaveWithToast();

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [showPreviousYear, setShowPreviousYear] = useState(false);
  // null while the list of the chosen year is being loaded.
  const [holidays, setHolidays] = useState<CompanyHoliday[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // The holiday form: null while closed, otherwise the draft it opens with.
  const [form, setForm] = useState<HolidayDraft | null>(null);

  const load = useCallback(async () => {
    try {
      setHolidays(await fetchCompanyHolidays(companyId, year));
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, [companyId, year]);

  useEffect(() => {
    setHolidays(null);
    load();
  }, [load]);

  const toggleActive = async (holiday: CompanyHoliday) => {
    await save(() =>
      saveCompanyHoliday(companyId, {
        id: holiday.id,
        title: holiday.title,
        startDate: holiday.startDate,
        endDate: holiday.endDate,
        isActive: !holiday.isActive,
      })
    );
    await load();
  };

  const importPublicHolidays = async () => {
    const alreadyListed = new Set(
      (holidays ?? []).map((holiday) => `${holiday.startDate}:${holiday.title}`)
    );
    const missing = getPublicHolidaysForYear(year).filter(
      (holiday) => !alreadyListed.has(`${holiday.startDate}:${holiday.title}`)
    );
    if (missing.length === 0) {
      toast.info(t("settings_holidays_import_nothing"));
      return;
    }

    await save(
      async () => {
        const results = await Promise.allSettled(
          missing.map((holiday) =>
            saveCompanyHoliday(companyId, { ...holiday, isActive: true })
          )
        );
        // Also after a failure: some of the holidays may have been saved, and a
        // retry must see them in the list or it would add them a second time.
        // Inside the save, so the button stays disabled until the list is fresh.
        await load();
        if (results.some((result) => result.status === "rejected")) {
          throw new Error("Some public holidays were not saved");
        }
      },
      { successMessage: t("settings_holidays_imported") }
    );
  };

  // The form belongs to the year it was opened in.
  const showYear = (chosenYear: number) => {
    setYear(chosenYear);
    setForm(null);
  };

  const showPreviousYearList = () => {
    setShowPreviousYear(true);
    showYear(currentYear - 1);
  };

  const closeFormAndReload = () => {
    setForm(null);
    load();
  };

  if (loadFailed) {
    return (
      <StatusBanner
        tone="error"
        title={t("settings_holidays_load_failed")}
        action={
          <button type="button" className={btnSecondaryCls} onClick={load}>
            {t("retry")}
          </button>
        }
      />
    );
  }

  const years = showPreviousYear
    ? [currentYear - 1, currentYear, currentYear + 1]
    : [currentYear, currentYear + 1];
  // A holiday that has begun is already counted in leave days, so the backend
  // refuses to change it.
  const today = format(new Date(), "yyyy-MM-dd");
  const isPast = (holiday: CompanyHoliday) => holiday.startDate < today;

  return (
    <div className="space-y-5">
      <SettingsCard
        title={t("settings_holidays_year_title", { year })}
        description={t("settings_holidays_year_lead")}
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={btnSecondaryCls}
              disabled={saving || holidays === null}
              onClick={importPublicHolidays}
            >
              {t("settings_holidays_import")}
            </button>
            <button
              type="button"
              className={btnSecondaryCls}
              onClick={() => setForm(NEW_HOLIDAY)}
            >
              {t("settings_holidays_add")}
            </button>
          </div>
        }
      >
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {years.map((listedYear) => (
            <button
              key={listedYear}
              type="button"
              aria-pressed={listedYear === year}
              disabled={holidays === null}
              className={listedYear === year ? btnPrimaryCls : btnSecondaryCls}
              onClick={() => showYear(listedYear)}
            >
              {listedYear}
            </button>
          ))}
          {!showPreviousYear && (
            <button type="button" className={btnLinkCls} onClick={showPreviousYearList}>
              {t("settings_holidays_show_previous_year", { year: currentYear - 1 })}
            </button>
          )}
        </div>

        {form && (
          <div className="mb-4">
            <HolidayForm
              // The form reads its values once; a new key gives a fresh form for another holiday.
              key={form.id ?? "new"}
              companyId={companyId}
              year={year}
              initial={form}
              onSaved={closeFormAndReload}
              onCancel={() => setForm(null)}
            />
          </div>
        )}

        {holidays === null ? (
          <p className="text-sm text-gray-500">{t("loading")}</p>
        ) : (
          <>
            <CatalogTable
              columns={[
                {
                  key: "date",
                  header: t("settings_holidays_col_date"),
                  className: "w-56 whitespace-nowrap",
                },
                { key: "title", header: t("settings_holidays_col_name") },
              ]}
              rows={holidays.map((holiday) => ({
                id: holiday.id,
                cells: {
                  date: formatDates(holiday),
                  title: holiday.isActive ? (
                    holiday.title
                  ) : (
                    <span className="text-gray-500">
                      {holiday.title} ({t("settings_holidays_turned_off")})
                    </span>
                  ),
                },
              }))}
              renderRowAction={(id) => {
                const holiday = holidays.find((listed) => listed.id === id);
                if (!holiday || isPast(holiday)) return null;
                return (
                  <span className="inline-flex items-center gap-4">
                    <ChangeLink
                      onClick={() =>
                        setForm({
                          id: holiday.id,
                          title: holiday.title,
                          startDate: holiday.startDate,
                          endDate: holiday.endDate,
                        })
                      }
                    />
                    <button
                      type="button"
                      className={clsx(btnLinkCls, "max-md:-my-3 max-md:py-3")}
                      disabled={saving}
                      onClick={() => toggleActive(holiday)}
                    >
                      {holiday.isActive
                        ? t("settings_holidays_turn_off")
                        : t("settings_holidays_turn_on")}
                    </button>
                  </span>
                );
              }}
              empty={t("settings_holidays_empty")}
            />
            {holidays.some(isPast) && (
              <p className="mt-3 text-sm text-gray-600">
                {t("settings_holidays_past_note")}
              </p>
            )}
          </>
        )}
      </SettingsCard>
    </div>
  );
}
